import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  HELOC_CUSTOMER_SERVICE_DISPLAY,
  HELOC_CUSTOMER_SERVICE_TEL,
  MORTGAGE_CUSTOMER_SERVICE_DISPLAY,
  MORTGAGE_CUSTOMER_SERVICE_TEL,
} from '@ld.consumer-portal/constants';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export interface PaymentReviewData {
  totalAmount: number;
  paymentDate: Date;
  dueDate: Date;
  bankAccountDisplay: string;
  accountHolderName: string;
  lastFourDigits: string;
  breakdown?: {
    principalAndInterest?: number;
    taxesAndInsurance?: number;
    additionalPrincipal?: number;
    additionalEscrow?: number;
    fees?: number;
  };
  loanNumber: string;
  isHeloc?: boolean;
}

@Component({
  selector: 'ld-payment-review-modal',
  imports: [BaseModalComponent, CurrencyPipe, MatIconModule, MatProgressSpinnerModule, TranslocoDirective],
  providers: [CurrencyPipe],
  templateUrl: './payment-review-modal.component.html',
  styleUrl: './payment-review-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentReviewModalComponent {
  private readonly currencyPipe = inject(CurrencyPipe);

  protected readonly customerServicePhone = computed(() =>
    this.paymentData().isHeloc ? HELOC_CUSTOMER_SERVICE_DISPLAY : MORTGAGE_CUSTOMER_SERVICE_DISPLAY,
  );
  protected readonly customerServiceTel = computed(() =>
    this.paymentData().isHeloc ? HELOC_CUSTOMER_SERVICE_TEL : MORTGAGE_CUSTOMER_SERVICE_TEL,
  );

  readonly paymentData = input.required<PaymentReviewData>();
  readonly isLoading = input<boolean>(false);
  readonly confirm = output<void>();
  readonly edit = output<void>();
  readonly close = output<void>();

  protected readonly isVisible = signal(true);
  protected readonly currentDate = new Date();

  protected readonly formattedTotalAmount = computed(
    () => this.currencyPipe.transform(this.paymentData().totalAmount, 'USD', 'symbol', '1.2-2') ?? '',
  );

  protected onConfirm(): void {
    if (!this.isLoading()) {
      this.confirm.emit();
    }
  }

  protected onEdit(): void {
    if (!this.isLoading()) {
      this.edit.emit();
    }
  }

  protected onClose(): void {
    if (!this.isLoading()) {
      this.isVisible.set(false);
      setTimeout(() => this.close.emit(), 200);
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  protected onTelClick(event: Event): void {
    const anchor = (event.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href') ?? '';
    if (href.startsWith('tel:')) {
      event.preventDefault();
      const suppress = (e: BeforeUnloadEvent): void => e.stopImmediatePropagation();
      window.addEventListener('beforeunload', suppress, { capture: true });
      window.location.href = href;
      setTimeout(() => window.removeEventListener('beforeunload', suppress, { capture: true }), 100);
    }
  }

  protected formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
